import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCourses } from '../contexts/CourseContext';
import { stripHtmlTags } from '../utils/textUtils';
import { supabase } from '../lib/supabase';

// 카테고리 목록 (네비게이션용)
const categories = [
  { id: 'group', name: '단체체험' },
  { id: 'preschool', name: '유치과정' },
  { id: 'elementary', name: '초등과정' },
  { id: 'secondary', name: '중·고등과정' },
  { id: 'adult', name: '성인과정' },
  { id: 'special', name: '특별과정' },
  { id: 'camp', name: '방학캠프' },
  { id: 'international', name: '국제교류' }
];

// 카테고리 정보 매핑
const categoryInfo: Record<string, { title: string; subtitle: string; description: string }> = {
  group: {
    title: '단체체험',
    subtitle: '글로벌 체험 프로그램 (Global Experience Program)',
    description: '영어·과학·문화가 융합된 1일형 체험학습 프로그램으로, 원어민 교사와 함께 영어·STEAM·AI 체험활동을 즐기며 창의력과 협동심, 글로벌 감각을 키웁니다.'
  },
  preschool: {
    title: '유치과정',
    subtitle: '유아 영어과정 (Early Learners English Program)',
    description: '스토리텔링, 노래, 놀이 중심의 오감형 영어체험 과정으로, 파닉스와 생활회화를 자연스럽게 익히며 언어감각·표현력·사회성을 함께 기릅니다.'
  },
  elementary: {
    title: '초등과정',
    subtitle: '초등 영어·창의과정 (Elementary English & STEAM Program)',
    description: '기초 영어회화, 파닉스, 독서·토론·쓰기 훈련을 중심으로 언어의 기초를 확립하고, STEAM·문화체험을 통해 탐구력과 창의력을 함께 키웁니다.'
  },
  secondary: {
    title: '중·고등과정',
    subtitle: '중·고등 글로벌 리더십과정 (Secondary Global Leadership Program)',
    description: '실전 영어 활용능력과 글로벌 리더십을 키우는 과정으로, 토론·스피치·에세이·ELTiS 대비를 통해 국제학교 진학 및 해외 교류를 준비합니다.'
  },
  adult: {
    title: '성인과정',
    subtitle: '성인 글로벌 커뮤니케이션 & 라이프 스킬 프로그램\n(Adult Global Communication & Life Skill Program)',
    description: '실생활·비즈니스·여행을 포함한 다양한 목적에 맞는 실용 영어회화부터 쿠킹 클래스, 글로벌 문화 체험, 테마 워크숍 등 국제 감각을 균형 있게 향상시키는 통합형 프로그램입니다.'
  },
  special: {
    title: '특별과정',
    subtitle: '글로벌 소통·교육 커뮤니티 프로그램\nGlobal Communication & Community Program',
    description: '글로벌 의사소통 강화 및 교육 협력을 위한 특별 프로그램입니다.'
  },
  camp: {
    title: '방학캠프',
    subtitle: '글로벌 교류 캠프 (Global Exchange Camp)',
    description: '국내 학생과 외국 학생이 함께하는 Korean International Camp로, 한국어·영어 공동 프로젝트, 문화교류, 봉사활동 등을 통해 진정한 국제 우정을 형성하는 체험형 글로벌 캠프입니다.'
  },
  international: {
    title: '국제교류',
    subtitle: '국제교류 프로그램 (International Exchange Program)',
    description: '해외 명문학교와의 교류, 단기 국제스쿨링 캠프, 외국인 장학생 초청 등 다양한 형태의 국제 교류 프로그램으로, 실질적인 언어·문화 교류를 통해 글로벌 역량을 함께 키워나갑니다.'
  }
};

const ProgramCategory: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const { courses, loading } = useCourses();
  // 프로그램별 소개글(단건 로드 결과) — key: program id
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});

  const info = category ? categoryInfo[category] : null;

  // 페이지 진입 또는 카테고리 변경 시 맨 위로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);

  // 카테고리에 맞는 프로그램 필터링 (프로그램 카탈로그용)
  const filteredCourses = courses.filter(course => {
    // 비공개 강의 제외
    if (!course.is_published) return false;

    // 프로그램 카탈로그 노출 설정 확인 (2025-01-27 추가)
    if (!course.show_in_catalog) return false;

    // 카테고리 매칭만 확인 (상태 필터/검색 제거됨)
    const matchesCategory = course.category?.includes(category as any) || false;
    return matchesCategory;
  });

  // 교육내용(description)은 목록 전체 조회에서 제외했으므로(부하 대응),
  // 카탈로그 소개글은 이 카테고리에 해당하는 프로그램 것만 따로 읽어온다.
  // 수강신청 페이지와 달리 여기는 한 화면에 몇 건뿐이라 부담이 없다.
  const categoryIds = filteredCourses.map(c => c.id).sort().join(',');
  useEffect(() => {
    if (!categoryIds) return;
    let cancelled = false;
    supabase
      .from('programs')
      .select('id,description')
      .in('id', categoryIds.split(','))
      .then(({ data }) => {
        if (cancelled || !data) return;
        setDescriptions(Object.fromEntries(data.map((d: any) => [d.id, d.description || ''])));
      });
    return () => { cancelled = true; };
  }, [categoryIds]);

  // 프로그램 카탈로그 정렬: catalog_order 우선, 그 다음 created_at (2025-01-27 추가)
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    // catalog_order가 있는 것 우선
    if (a.catalog_order !== undefined && b.catalog_order === undefined) return -1;
    if (a.catalog_order === undefined && b.catalog_order !== undefined) return 1;

    // 둘 다 catalog_order가 있으면 오름차순
    if (a.catalog_order !== undefined && b.catalog_order !== undefined) {
      if (a.catalog_order !== b.catalog_order) {
        return a.catalog_order - b.catalog_order;
      }
    }

    // catalog_order가 같거나 둘 다 없으면 created_at 내림차순 (최신순)
    return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
  });

  if (!info) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">카테고리를 찾을 수 없습니다</h2>
            <Link to="/programs" className="text-sm sm:text-base text-green-600 hover:underline">
              프로그램 목록으로 돌아가기 →
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header */}
      <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920&h=600&fit=crop&q=80"
            alt={info.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">프로그램</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">{info.title}</h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-4 sm:mb-6 whitespace-pre-line">{info.subtitle}</p>
          <div className="w-16 sm:w-20 h-1 bg-white/70 mb-4 sm:mb-6"></div>
          <p className="text-sm sm:text-base md:text-lg text-gray-100 leading-relaxed max-w-4xl">
            {info.description}
          </p>
        </div>
      </div>

      {/* Category Navigation Tabs */}
      <div className="bg-gray-100 border-y border-gray-200 py-3 sm:py-4 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 sm:gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => navigate(`/programs/category/${cat.id}`)}
                className={`px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  category === cat.id
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Programs Grid - 큰 카드 2열 레이아웃 */}
      <div className="bg-gray-50 px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          {/* 결과 카운트 */}
          <div className="mb-6 sm:mb-8">
            <p className="text-base sm:text-lg text-gray-600">
              총 <span className="font-bold text-emerald-600">{sortedCourses.length}</span>개의 프로그램
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : sortedCourses.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <p className="text-gray-500 text-base sm:text-lg mb-3 sm:mb-4">서비스 준비중입니다.</p>
              <Link to="/programs" className="text-sm sm:text-base text-green-600 hover:underline">
                전체 프로그램 보기 →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {sortedCourses.map((course) => {
                const description = stripHtmlTags(descriptions[course.id] || course.courseDescription || '');
                const truncatedDescription = description.length > 200
                  ? description.substring(0, 200) + '...'
                  : description;

                return (
                  <div
                    key={course.id}
                    onClick={() => navigate(`/programs/${course.id}`)}
                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  >
                    {/* 이미지 */}
                    <div className="relative h-80 overflow-hidden bg-gray-200">
                      {course.courseImage ? (
                        <img
                          src={course.courseImage}
                          alt={course.courseName}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-400 to-emerald-600">
                          <span className="text-6xl text-white/20">📚</span>
                        </div>
                      )}
                    </div>

                    {/* 내용 */}
                    <div className="p-8">
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors">
                        {course.courseName}
                      </h3>

                      <p className="text-gray-600 text-base leading-relaxed mb-6 line-clamp-4">
                        {truncatedDescription}
                      </p>

                      {/* 자세히 보기 버튼 */}
                      <div className="flex items-center text-emerald-600 font-semibold group-hover:gap-3 gap-2 transition-all">
                        <span>자세히 보기</span>
                        <svg className="h-5 w-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProgramCategory;
